import { CopyOutlined, SyncOutlined } from '@ant-design/icons';
import { Alert, Flex, Modal, Tag } from 'antd';
import clsx from 'clsx';
import { BaseButton } from 'components/button/button-base';
import { Text } from 'components/mvp-typography';
import { useState } from 'react';
import {
  ActionState,
  ActionTagRenderProps,
  ActionTagSettings
} from '../../../../../../components/action-tag/action-tag-component';
import { useGetInvitationLink } from '../../../../../../lib/hooks/tanstack/use-GetInvitationLink';
import { ErrorDisplay } from 'components/ErrorDisplay';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { clinicActionGetInvitationLink } from 'lib/api/firebase/apis/apis';
/**
 * A helper method to generate a "Copy invite link" action tag
 * @param copyAction
 * @returns
 */
export const ACTION_COPYINVITELINK: (
  copyAction: (value: string) => Promise<void> | void
) => ActionTagSettings<any> = copyAction => ({
  name: 'Copy invite link',
  shouldShow: p => p.invitePending,
  initialState: () => ActionState.NEUTRAL,
  onSuccessSettings: {
    blockOnDone: false,
    timeout: 2000
  },
  render: CopyInviteLinkActionTag,
  onClick: async p => {
    if (!p.raw.id) {
      throw new Error('ID is undefined. Please try reloading the page');
    }
    const link = await clinicActionGetInvitationLink({
      invitationId: p.raw.id
    });

    await copyAction(link.data);
    return link;
  }
});

export function CopyInviteLinkActionTag(props: ActionTagRenderProps<any>) {
  const [isOpen, setIsOpen] = useState(false);

  const actionState = props.actionState;
  const invitationLink = useGetInvitationLink(
    props.record.raw.id,
    // Only query when the modal is opened
    isOpen == true
  );

  function openModal() {
    setIsOpen(true);
  }
  function closeModal() {
    setIsOpen(false);
  }
  return (
    <div>
      <Tag
        onClick={openModal}
        color={props.tagStyles.color}
        icon={props.tagStyles.icon}
        className={clsx('clickable', props.disabled && 'disabled')}
      >
        {props.name}
      </Tag>
      <Modal
        open={isOpen}
        onClose={closeModal}
        onCancel={closeModal}
        footer={() => <></>}
        closable={actionState !== ActionState.PROCESSING}
      >
        <Flex gap={12} vertical>
          <div>
            <Text>Invitation link for: {props.record.email}</Text>

            <Alert
              type="warning"
              showIcon
              message={
                <div>
                  <Text>This is a sensitive link.</Text>
                </div>
              }
              description={
                <Flex gap={16} vertical>
                  <Text>
                    This link allows the invitee to accept their invitation and
                    create their account on ConcussionRx. This panel should only
                    be used when the invitee is not able to receive the
                    invitation through the normal email and SMS invitation
                    flows.
                  </Text>
                  <Text>
                    Improper sharing of this link can result in impersonation
                    until the invitation is accepted.{' '}
                    <b>
                      Do not share this secret with anyone except for the
                      intended invitee.
                    </b>
                  </Text>
                </Flex>
              }
            />
          </div>

          <div
            style={{
              position: 'relative'
            }}
          >
            <textarea
              style={{
                border: '1px solid black',
                borderRadius: 6,
                width: '100%'
              }}
              readOnly
              disabled
              value={
                invitationLink.isLoading
                  ? 'Generating invitation link...'
                  : invitationLink.isError
                  ? 'Failed to generate an invitation link'
                  : invitationLink.data?.data
              }
            />
            {invitationLink.isLoading && (
              <div
                style={{
                  position: 'absolute',
                  right: 12,
                  top: 0
                }}
              >
                <SyncOutlined spin />
              </div>
            )}
          </div>
          {invitationLink.error && (
            <ErrorDisplay
              title={`Failed to generate an invitation link for ${props.record.email}`}
              description={`Please report the full content of this error dialog to the CRx team.`}
              error={resolveError(invitationLink.error)}
            />
          )}

          <BaseButton
            onClick={() => props.onClick(props.record)}
            style={{
              padding: 0,
              ...(actionState === ActionState.SUCCESS && {
                background: `var(--ant-color-${props.tagStyles.color})`
              })
            }}
            disabled={actionState === ActionState.PROCESSING}
          >
            {
              /**
               * Render the icon
               */
              props.tagStyles.icon
            }
            {actionState === ActionState.NEUTRAL
              ? 'Copy'
              : actionState === ActionState.PROCESSING
              ? 'Copying'
              : actionState === ActionState.SUCCESS
              ? 'Copied'
              : 'Failed to copy'}{' '}
            to clipboard <CopyOutlined />
          </BaseButton>
        </Flex>
      </Modal>
    </div>
  );
}
